import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";

// Menu drops in from the field and fades away; same feel as the country picker.
const menuMotion = {
    initial: { opacity: 0, y: -6, scale: 0.98 },
    animate: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.2, ease: [0.22, 1, 0.36, 1] } },
    exit: { opacity: 0, y: -4, transition: { duration: 0.14 } },
};

const IconSelect = ({
    value,
    onSelect,
    validationError,
    options,
    placeholder = "Select",
    hClassName = "tw:!h-[50px]"
}) => {
    const { t } = useTranslation();
    const wrapperRef = useRef(null);

    const [open, setOpen] = useState(false);

    const selectedLabel = options.find((item) => item.value === value)?.label;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);


    return (
        <div ref={wrapperRef} className="tw:!relative">
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                className={`
                    tw:!flex
                    tw:!items-center
                    tw:!w-full
                    ${hClassName}
                    tw:!px-3
                    tw:!border
                    tw:!rounded
                    tw:!bg-white
                    tw:!text-left
                    tw:outline-none
                    tw:transition-[border-color,box-shadow] tw:duration-200
                    tw:focus-visible:shadow-[0_0_0_3px_rgba(219,190,120,0.18)]
                    tw:text-sm
                    tw:text-[#4E4E4E]
                    tw:font-[var(--font-sans-primary)]
                    ${open ? "tw:!border-[#DBBE78] tw:shadow-[0_0_0_3px_rgba(219,190,120,0.18)]" : "tw:!border-[#DADADA]"}   
                `}
            >
                <span className={`tw:!flex-1 tw:!text-[15px] tw:!font-[400] ${selectedLabel ? "tw:!text-[#4E4E4E]" : "tw:!text-[#A7A7A7]"}`}>
                    {selectedLabel ? t(selectedLabel) : placeholder}
                </span>

                {/* arrow */}
                <svg
                    className={`tw:!w-4 tw:!h-4 tw:!ml-2 tw:!transition-transform ${open ? "tw:!rotate-180" : ""}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                >
                    <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                        clipRule="evenodd"
                    />
                </svg>
            </button>

            <AnimatePresence>
            {open && (
                <motion.ul
                    {...menuMotion}
                    style={{ transformOrigin: "top" }}
                    className="
                        tw:!absolute
                        tw:!z-10
                        tw:!mt-1
                        tw:!w-full
                        tw:!bg-white
                        tw:!border
                        tw:!border-[#EFE6D2]
                        tw:!border-t-2 tw:!border-t-[#DBBE78]
                        tw:!rounded-lg
                        tw:!shadow-[0_18px_40px_-12px_rgba(40,40,40,0.28)]
                        tw:!overflow-hidden
                        tw:!pl-0
                    "
                >
                    {options.map((item) => {
                        const isSelected = item.value === value;

                        return (
                            <li
                                key={item.value}
                                onClick={() => {
                                    onSelect(item.value);
                                    setOpen(false);
                                }}
                                className={`
                                    tw:!flex
                                    tw:!items-center
                                    tw:!justify-between
                                    tw:!px-3
                                    tw:!py-2
                                    tw:!cursor-pointer
                                    tw:!transition-colors
                                    tw:hover:bg-[#FBF6EA]
                                    tw:text-[#4E4E4E] 
                                    tw:font-[var(--font-sans-primary)] 
                                    tw:!text-sm
                                    ${isSelected
                                        ? "tw:!bg-[#f9edd3]"
                                        : "tw:!text-gray-700"
                                    }
                                `}
                            >
                                <span className="tw:!text-sm">{t(item.label)}</span>

                                {isSelected && (
                                    <svg
                                        className="tw:!w-4 tw:!h-4 tw:text-[#DBBE78]"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 011.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                )}
                            </li>
                        );
                    })}
                </motion.ul>
            )}
            </AnimatePresence>

            {validationError && (
                <small className="tw:!text-red-500">
                    {t(validationError)}
                </small>
            )}
        </div>
    );
};

export default IconSelect;
